// Watabaga NPC MENU (buyer view) — the food the vendor currently offers, each line showing its price
// and how many are in stock (the building fridge). Buying pulls one from the fridge and pays the owner;
// "Sold out" when the fridge is empty. The owner curates WHICH items appear from the fridge panel
// (setMenu) — this panel is purely the customer-facing menu. Fed by foodMenuStore.

import { ITEM_NAMES, buildingName, isBuildingId } from "@ghostland/shared";
import { injectTheme } from "./theme.js";
import { foodMenuStore } from "../items/foodMenuStore.js";
import { balanceStore } from "../items/balanceStore.js";
import { itemThumb } from "../items/itemThumb.js";
import { bankIcon } from "./pixelBills.js";

export interface FoodShopActions {
  buy: (itemId: string) => void;
}
let actions: FoodShopActions | null = null;
export function setFoodShopActions(a: FoodShopActions): void { actions = a; }

const STYLE_ID = "ghostland-foodshop-style";
const CSS = `
#gl-fshop-overlay { position: fixed; inset: 0; z-index: 30; display: none; align-items: center; justify-content: center;
  background: rgba(5,0,7,.55); font-family: ui-monospace, "SFMono-Regular", Menlo, monospace; color: var(--gl-text); }
#gl-fshop-overlay.open { display: flex; }
#gl-fshop-card { width: 420px; max-width: 92vw; max-height: 82vh; display: flex; flex-direction: column;
  background: var(--gl-panel); border: 1px solid var(--gl-border); border-radius: 12px; box-shadow: 0 16px 50px rgba(0,0,0,.7); }
#gl-fshop-head { padding: 16px 18px 12px; border-bottom: 1px solid var(--gl-border); display: flex; align-items: baseline; justify-content: space-between; }
#gl-fshop-head h1 { margin: 0; font-size: 15px; letter-spacing: .16em; }
#gl-fshop-cash { font-size: 12px; color: var(--gl-muted); display: inline-flex; align-items: center; gap: 5px; }
#gl-fshop-list { padding: 10px 14px; overflow-y: auto; }
.gl-fshop-row { display: flex; align-items: center; gap: 10px; padding: 8px 10px; border-radius: 8px;
  border: 1px solid transparent; }
.gl-fshop-row:hover { background: var(--gl-panel-2); }
.gl-fshop-row canvas { width: 34px; height: 34px; image-rendering: pixelated; }
.gl-fshop-name { flex: 1; font-size: 13px; }
.gl-fshop-name .stock { font-size: 11px; color: var(--gl-muted); margin-top: 2px; }
.gl-fshop-name .stock.out { color: #e06a6a; }
.gl-fshop-price { font-size: 12px; color: var(--gl-muted); margin-right: 4px; }
.gl-fshop-buy { font-size: 12px; padding: 6px 14px; border-radius: 7px; border: 1px solid var(--gl-accent);
  background: var(--gl-accent); color: var(--gl-accent-fg); cursor: pointer; font-family: inherit; }
.gl-fshop-buy:hover:not(:disabled) { background: var(--gl-accent-hover); }
.gl-fshop-buy:disabled { opacity: .4; cursor: default; background: var(--gl-panel-2); color: var(--gl-muted); border-color: var(--gl-border); }
#gl-fshop-empty { color: var(--gl-muted); font-size: 12px; padding: 16px 6px; text-align: center; }
#gl-fshop-close { margin: 6px 18px 16px; padding: 8px 0; background: none; border: 1px solid var(--gl-border);
  border-radius: 6px; color: var(--gl-muted); cursor: pointer; font-family: inherit; font-size: 11px; }
`;

let overlay: HTMLDivElement | null = null;
let listEl: HTMLDivElement | null = null;
let cashEl: HTMLDivElement | null = null;
let headEl: HTMLHeadingElement | null = null;
let unsub: Array<() => void> = [];

function refresh(): void {
  if (!listEl) return;
  const menu = foodMenuStore.get();
  const items = menu.items.filter((i) => i.onMenu);
  if (headEl) headEl.textContent = isBuildingId(menu.buildingId) ? buildingName(menu.buildingId).toUpperCase() : "STORE";
  const cash = balanceStore.get();
  if (cashEl) cashEl.innerHTML = `${bankIcon()} <b>${cash.toLocaleString()}</b>`;
  if (items.length === 0) { listEl.innerHTML = `<div id="gl-fshop-empty">Nothing on the menu right now.</div>`; return; }
  listEl.innerHTML = "";
  for (const it of items) {
    const row = document.createElement("div"); row.className = "gl-fshop-row";
    const icon = itemThumb(it.itemId); icon.style.imageRendering = "pixelated"; row.appendChild(icon); // real garment art for clothes
    const name = document.createElement("div"); name.className = "gl-fshop-name";
    const out = it.stock <= 0;
    name.innerHTML = `${ITEM_NAMES[it.itemId as keyof typeof ITEM_NAMES] ?? it.itemId}<div class="stock${out ? " out" : ""}">${out ? "SOLD OUT" : `${it.stock} in stock`}</div>`;
    row.appendChild(name);
    const price = document.createElement("div"); price.className = "gl-fshop-price"; price.textContent = `$${it.price.toLocaleString()}`;
    row.appendChild(price);
    const buy = document.createElement("button"); buy.className = "gl-fshop-buy"; buy.textContent = "Buy";
    buy.disabled = out || cash < it.price;
    buy.addEventListener("click", () => actions?.buy(it.itemId));
    row.appendChild(buy);
    listEl.appendChild(row);
  }
}

function ensure(): void {
  injectTheme();
  if (!document.getElementById(STYLE_ID)) { const s = document.createElement("style"); s.id = STYLE_ID; s.textContent = CSS; document.head.appendChild(s); }
  if (overlay) return;
  overlay = document.createElement("div");
  overlay.id = "gl-fshop-overlay";
  overlay.innerHTML = `<div id="gl-fshop-card">
    <div id="gl-fshop-head"><h1>WATABAGA</h1><div id="gl-fshop-cash"></div></div>
    <div id="gl-fshop-list"></div>
    <button id="gl-fshop-close">Close (Esc)</button>
  </div>`;
  document.body.appendChild(overlay);
  listEl = overlay.querySelector("#gl-fshop-list");
  cashEl = overlay.querySelector("#gl-fshop-cash");
  headEl = overlay.querySelector("#gl-fshop-head h1");
  overlay.querySelector("#gl-fshop-close")!.addEventListener("click", closeFoodShop);
  overlay.addEventListener("click", (e) => { if (e.target === overlay) closeFoodShop(); });
}

export function openFoodShop(): void {
  ensure();
  overlay!.classList.add("open");
  unsub = [foodMenuStore.subscribe(refresh), balanceStore.subscribe(refresh)];
}
export function closeFoodShop(): void {
  overlay?.classList.remove("open");
  unsub.forEach((fn) => fn()); unsub = [];
}
export function isFoodShopOpen(): boolean { return overlay?.classList.contains("open") ?? false; }
